/**
 * /admin/users — list, search, role-edit, disable/enable, create,
 * AND approve/reject sign-ups.  The old /admin/pending tab folded
 * into this view when the Setup Pipeline panel landed: the KPI
 * panel's "Awaiting approval" chip selects ``status=pending`` here,
 * and pending rows surface Approve / Reject in the kebab.
 *
 * Default filter is "All" so the table count lines up with the
 * Total users KPI on landing.  Admins narrow via the Status
 * dropdown or the clickable KPI chips above.
 *
 * Premium pass:
 *  - Toolbar + table wrapped in ivory_light cards with gold-accent
 *    top strip, matching the Profile-page treatment.
 *  - Proper MUI Select (with InputLabel) instead of native <option>
 *    — eliminates the label/placeholder overlap.
 *  - Mini-avatar column on each user row.
 *  - Role chips use the brand-gold outline treatment.
 *  - Empty-state card with icon + helper copy + CTA.
 */

import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RestoreIcon from '@mui/icons-material/Restore';
import LockResetIcon from '@mui/icons-material/LockReset';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  fetchRoles,
  fetchUsers,
  type SortDir,
  type UserListFilters,
  type UserListItem,
  type UserSortKey,
} from '../../api/admin';
import { ROLES_ALONGSIDE_ADMIN } from '../../auth/roles';
import { useAuth } from '../../auth/useAuth';
import { CreateUserDialog } from './CreateUserDialog';
import { DeleteUserDialog } from './DeleteUserDialog';
import { EditProfileDialog } from './EditProfileDialog';
import { EditRolesDialog } from './EditRolesDialog';
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { StatusUpdateDialog, type StatusUpdateMode } from './StatusUpdateDialog';
import { UserActivityDrawer } from './UserActivityDrawer';

const INK = '#0f0a07';
const IVORY_LIGHT = '#fdf9f0';
const GOLD = '#bb8c3f';
const GOLD_LIGHT = '#e9c46a';
const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';

const PAGE_SIZE = 25;

/**
 * Shared MUI-Select `MenuProps` for the toolbar dropdowns. Themes the
 * popup paper to match the rest of the admin section (warm ivory with
 * a gold accent strip + gold hover wash on items).
 */
const selectMenuProps = {
  PaperProps: {
    sx: {
      mt: 0.75,
      // Keep the dropdown proportional to the trigger: MUI Select sets
      // ``style.minWidth`` to the anchor's offsetWidth (~160px from
      // the FormControl), so we just need a tight ``maxWidth`` so the
      // paper doesn't stretch wider than the longest option needs.
      width: 'auto',
      maxWidth: 220,
      bgcolor: '#fdf9f0',
      backgroundImage: 'none',
      borderRadius: 1.5,
      border: '1px solid rgba(187, 140, 63, 0.25)',
      boxShadow: '0 14px 32px rgba(142, 106, 42, 0.12)',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
        zIndex: 1,
      },
      '& .MuiList-root': { py: 0.75 },
      '& .MuiMenuItem-root': {
        fontFamily: FONT_INTER,
        fontSize: 13,
        mx: 0.75,
        px: 1.25,
        py: 0.85,
        borderRadius: 1,
        color: INK,
        transition: 'background-color 140ms ease, color 140ms ease',
        '&:hover': {
          bgcolor: 'rgba(187, 140, 63, 0.10)',
        },
        '&.Mui-selected': {
          bgcolor: 'rgba(187, 140, 63, 0.16)',
          color: '#8e6a2a',
          fontWeight: 600,
          '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.22)' },
        },
      },
    },
  },
} as const;

type StatusFilter = 'pending' | 'active' | 'disabled' | 'rejected' | 'all';
type SetupFilter = 'completed' | 'pending_setup' | 'link_expired' | 'all';

export function UsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>(() => searchParams.get('role') ?? '');
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState<UserSortKey>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // statusFilter is URL-derived — same single-source-of-truth pattern
  // as setupFilter.  The Status dropdown's onChange calls
  // setSearchParams directly via setStatusFilter() rather than
  // updating local state and waiting for a sync effect.  Avoids the
  // bidirectional sync that produced a visible flicker on chip clicks
  // and on the Awaiting-approval chip changing ?status= from the
  // KPI panel.
  const statusFilter: StatusFilter = (() => {
    const raw = searchParams.get('status');
    return raw === 'pending' ||
      raw === 'active' ||
      raw === 'disabled' ||
      raw === 'rejected' ||
      raw === 'all'
      ? raw
      : 'all';
  })();
  const setStatusFilter = (next: StatusFilter) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'all') params.delete('status');
        else params.set('status', next);
        return params;
      },
      { replace: true },
    );
    setPage(0);
  };

  // setupFilter is URL-derived ONLY — no useState, no sync effects.
  // The Setup Pipeline chips above (in AdminLayout) drive it via
  // navigation; there's no in-table dropdown for it.  Keeping a
  // separate state mirror caused a bidirectional sync between two
  // useEffects (URL→state and state→URL), each triggering the other
  // for one render, producing a visible flicker on every chip click.
  // Reading directly from the URL each render keeps the change to
  // a single render pass.
  const setupFilter: SetupFilter = (() => {
    const raw = searchParams.get('setup');
    return raw === 'completed' || raw === 'pending_setup' || raw === 'link_expired' ? raw : 'all';
  })();

  // Keep the URL in lockstep with the role filter so links from
  // /admin/roles ("View all" cards) hydrate correctly + reflect
  // toolbar changes back in the address bar.  statusFilter and
  // setupFilter are URL-derived (their setters write to the URL
  // directly), so this effect only owns the role param.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (roleFilter) next.set('role', roleFilter);
        else next.delete('role');
        return next;
      },
      { replace: true },
    );
  }, [roleFilter, setSearchParams]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editRolesUser, setEditRolesUser] = useState<UserListItem | null>(null);
  const [editProfileUser, setEditProfileUser] = useState<UserListItem | null>(null);
  const [statusUser, setStatusUser] = useState<{
    user: UserListItem;
    mode: StatusUpdateMode;
  } | null>(null);
  const [resetUser, setResetUser] = useState<{
    user: UserListItem;
    mode: 'reset' | 'resend';
  } | null>(null);
  const [activityUser, setActivityUser] = useState<UserListItem | null>(null);
  const [deleteUserTarget, setDeleteUserTarget] = useState<UserListItem | null>(null);

  const filters = useMemo<UserListFilters>(() => {
    const f: UserListFilters = {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      sort_by: sortBy,
      sort_dir: sortDir,
    };
    if (statusFilter !== 'all') f.status = statusFilter;
    if (setupFilter !== 'all') f.setup_status = setupFilter;
    if (roleFilter) f.role = roleFilter;
    if (search.trim()) f.search = search.trim();
    return f;
  }, [search, statusFilter, setupFilter, roleFilter, page, sortBy, sortDir]);

  const onSort = (key: UserSortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'last_sign_in' || key === 'created' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const usersQuery = useQuery({
    queryKey: ['admin', 'users', filters],
    queryFn: () => fetchUsers(filters),
  });
  const rolesQuery = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: fetchRoles,
  });

  const users = usersQuery.data?.items ?? [];
  const total = usersQuery.data?.total ?? 0;
  const roles = rolesQuery.data?.items ?? [];

  return (
    <Stack spacing={2.5}>
      {/* ─── Toolbar ───────────────────────────────────────── */}
      <PremiumCard>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'center' },
            gap: 2,
            p: 2,
          }}
        >
          <TextField
            placeholder="Search email or name"
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'rgba(15, 10, 7, 0.4)' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flexGrow: 1,
              minWidth: 220,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 250, 235, 0.55)',
                fontFamily: FONT_INTER,
                fontSize: 14,
                transition: 'background-color 160ms ease',
                '& fieldset': { borderColor: 'rgba(15, 10, 7, 0.14)' },
                '&:hover': { bgcolor: 'rgba(255, 250, 235, 0.85)' },
                '&:hover fieldset': { borderColor: 'rgba(187, 140, 63, 0.4)' },
                '&.Mui-focused': { bgcolor: '#fffdf6' },
                '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: 1.5 },
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontFamily: FONT_INTER, fontSize: 13.5 }}>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              MenuProps={selectMenuProps}
              sx={{
                bgcolor: 'rgba(255, 250, 235, 0.55)',
                fontFamily: FONT_INTER,
                fontSize: 14,
                transition: 'background-color 160ms ease',
                '& fieldset': { borderColor: 'rgba(15, 10, 7, 0.14)' },
                '&:hover': { bgcolor: 'rgba(255, 250, 235, 0.85)' },
                '&:hover fieldset': { borderColor: 'rgba(187, 140, 63, 0.4)' },
                '&.Mui-focused': { bgcolor: '#fffdf6' },
                '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: 1.5 },
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Awaiting approval</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontFamily: FONT_INTER, fontSize: 13.5 }}>Role</InputLabel>
            <Select
              label="Role"
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(0);
              }}
              renderValue={(v) =>
                v ? (roles.find((r) => r.name === v)?.display_name ?? v) : 'All roles'
              }
              MenuProps={selectMenuProps}
              sx={{
                bgcolor: 'rgba(255, 250, 235, 0.55)',
                fontFamily: FONT_INTER,
                fontSize: 14,
                transition: 'background-color 160ms ease',
                '& fieldset': { borderColor: 'rgba(15, 10, 7, 0.14)' },
                '&:hover': { bgcolor: 'rgba(255, 250, 235, 0.85)' },
                '&:hover fieldset': { borderColor: 'rgba(187, 140, 63, 0.4)' },
                '&.Mui-focused': { bgcolor: '#fffdf6' },
                '&.Mui-focused fieldset': { borderColor: GOLD, borderWidth: 1.5 },
              }}
            >
              <MenuItem value="">All roles</MenuItem>
              {roles.map((r) => (
                <MenuItem key={r.id} value={r.name}>
                  {r.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              flexShrink: 0,
              background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
              color: INK,
              fontFamily: FONT_INTER,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontSize: 12,
              px: 2.25,
              py: 0.95,
              boxShadow: 'none',
              '&:hover': {
                background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
                boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
              },
            }}
          >
            Add User
          </Button>
        </Box>
      </PremiumCard>

      {/* ─── Table ─────────────────────────────────────────── */}
      <PremiumCard>
        {usersQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: GOLD }} />
          </Box>
        ) : usersQuery.error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {(usersQuery.error as Error).message}
          </Alert>
        ) : users.length === 0 ? (
          <EmptyState
            onNewUser={() => setCreateOpen(true)}
            hasFilters={!!search || statusFilter !== 'active' || !!roleFilter}
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      fontFamily: FONT_INTER,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      color: 'rgba(15, 10, 7, 0.5)',
                      borderBottom: '1px solid rgba(15, 10, 7, 0.08)',
                      bgcolor: 'rgba(187, 140, 63, 0.04)',
                      py: 1.5,
                    },
                  }}
                >
                  <SortableHeader
                    label="User"
                    sortKey="name"
                    currentSort={sortBy}
                    currentDir={sortDir}
                    onSort={onSort}
                  />
                  <SortableHeader
                    label="Status"
                    sortKey="status"
                    currentSort={sortBy}
                    currentDir={sortDir}
                    onSort={onSort}
                  />
                  <TableCell>Roles</TableCell>
                  <SortableHeader
                    label="Last sign-in"
                    sortKey="last_sign_in"
                    currentSort={sortBy}
                    currentDir={sortDir}
                    onSort={onSort}
                  />
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    isSelf={user.id === currentUser?.id}
                    onRowClick={() => setActivityUser(user)}
                    onEditProfile={() => setEditProfileUser(user)}
                    onEditRoles={() => setEditRolesUser(user)}
                    onApprove={() => setStatusUser({ user, mode: 'approve' })}
                    onReject={() => setStatusUser({ user, mode: 'reject' })}
                    onDisable={() => setStatusUser({ user, mode: 'disable' })}
                    onEnable={() => setStatusUser({ user, mode: 'enable' })}
                    onRestore={() => setStatusUser({ user, mode: 'restore' })}
                    onResetPassword={() => setResetUser({ user, mode: 'reset' })}
                    onResendInvitation={() => setResetUser({ user, mode: 'resend' })}
                    onDelete={() => setDeleteUserTarget(user)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTop: '1px solid rgba(15, 10, 7, 0.08)',
              px: 2.25,
              py: 1.25,
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 12,
                color: 'rgba(15, 10, 7, 0.55)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                sx={{ fontFamily: FONT_INTER, fontSize: 12.5, textTransform: 'none' }}
              >
                Previous
              </Button>
              <Button
                size="small"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
                sx={{ fontFamily: FONT_INTER, fontSize: 12.5, textTransform: 'none' }}
              >
                Next
              </Button>
            </Stack>
          </Box>
        )}
      </PremiumCard>

      {/* Dialogs */}
      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} roles={roles} />
      {editRolesUser && (
        <EditRolesDialog
          open
          user={editRolesUser}
          roles={roles}
          onClose={() => setEditRolesUser(null)}
        />
      )}
      {editProfileUser && (
        <EditProfileDialog open user={editProfileUser} onClose={() => setEditProfileUser(null)} />
      )}
      {statusUser && (
        <StatusUpdateDialog
          open
          user={statusUser.user}
          mode={statusUser.mode}
          onClose={() => setStatusUser(null)}
        />
      )}
      {resetUser && (
        <ResetPasswordDialog
          open
          user={resetUser.user}
          mode={resetUser.mode}
          onClose={() => setResetUser(null)}
        />
      )}
      <UserActivityDrawer
        open={!!activityUser}
        user={activityUser}
        onClose={() => setActivityUser(null)}
      />
      {deleteUserTarget && (
        <DeleteUserDialog open user={deleteUserTarget} onClose={() => setDeleteUserTarget(null)} />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------

function PremiumCard({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: IVORY_LIGHT,
        border: '1px solid rgba(187, 140, 63, 0.18)',
        borderRadius: 2,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
        },
      }}
    >
      {children}
    </Box>
  );
}

interface UserRowProps {
  user: UserListItem;
  isSelf: boolean;
  onRowClick: () => void;
  onEditProfile: () => void;
  onEditRoles: () => void;
  onApprove: () => void;
  onReject: () => void;
  onDisable: () => void;
  onEnable: () => void;
  onRestore: () => void;
  onResetPassword: () => void;
  onResendInvitation: () => void;
  onDelete: () => void;
}

const INACTIVE_DAYS = 30;
const INACTIVE_MS = INACTIVE_DAYS * 24 * 60 * 60 * 1000;

function UserRow({
  user,
  isSelf,
  onRowClick,
  onEditProfile,
  onEditRoles,
  onApprove,
  onReject,
  onDisable,
  onEnable,
  onRestore,
  onResetPassword,
  onResendInvitation,
  onDelete,
}: UserRowProps) {
  const initial = ((user.first_name || user.email)[0] ?? '?').toUpperCase();
  const inactive =
    user.status === 'active' &&
    !!user.last_login_at &&
    Date.now() - new Date(user.last_login_at).getTime() > INACTIVE_MS;

  return (
    <TableRow
      hover
      sx={{
        '& td': { borderBottom: '1px solid rgba(15, 10, 7, 0.05)', py: 1.5 },
        '&:hover': { bgcolor: 'rgba(187, 140, 63, 0.03)' },
      }}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
              color: INK,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: FONT_INTER,
              fontSize: 13,
              fontWeight: 700,
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
              flexShrink: 0,
            }}
          >
            {initial}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT_INTER,
                fontSize: 13.5,
                fontWeight: 600,
                color: INK,
                lineHeight: 1.25,
              }}
            >
              {user.first_name} {user.last_name}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: 11.5,
                color: 'rgba(15, 10, 7, 0.55)',
              }}
            >
              {user.email}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <StatusBadge status={user.status} setupStatus={user.setup_status} />
      </TableCell>
      <TableCell>
        <RolesCell roles={user.roles} />
      </TableCell>
      <TableCell
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 12,
          color: 'rgba(15, 10, 7, 0.6)',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box>
            {user.last_login_at
              ? new Date(user.last_login_at).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : '—'}
          </Box>
          {inactive && (
            <Tooltip title={`No sign-in in ${INACTIVE_DAYS}+ days`} arrow>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.4,
                  px: 0.7,
                  py: 0.15,
                  borderRadius: 0.5,
                  bgcolor: 'rgba(187, 140, 63, 0.12)',
                  color: '#a07530',
                  fontFamily: FONT_INTER,
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                }}
              >
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#bb8c3f' }} />
                Inactive
              </Box>
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center">
          <Button
            size="small"
            onClick={onRowClick}
            startIcon={<HistoryOutlinedIcon sx={{ fontSize: 15 }} />}
            sx={{
              px: 1.1,
              py: 0.35,
              fontFamily: FONT_INTER,
              fontSize: 11.5,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'none',
              color: GOLD,
              bgcolor: 'transparent',
              border: '1px solid rgba(187, 140, 63, 0.28)',
              borderRadius: 1,
              '&:hover': {
                bgcolor: 'rgba(187, 140, 63, 0.10)',
                borderColor: 'rgba(187, 140, 63, 0.5)',
              },
            }}
          >
            View activity
          </Button>
          <UserActionsMenu
            user={user}
            isSelf={isSelf}
            onEditProfile={onEditProfile}
            onEditRoles={onEditRoles}
            onApprove={onApprove}
            onReject={onReject}
            onResetPassword={onResetPassword}
            onResendInvitation={onResendInvitation}
            onDisable={onDisable}
            onEnable={onEnable}
            onRestore={onRestore}
            onDelete={onDelete}
          />
        </Stack>
      </TableCell>
    </TableRow>
  );
}

interface UserActionsMenuProps {
  user: UserListItem;
  isSelf: boolean;
  onEditProfile: () => void;
  onEditRoles: () => void;
  onApprove: () => void;
  onReject: () => void;
  onResetPassword: () => void;
  onResendInvitation: () => void;
  onDisable: () => void;
  onEnable: () => void;
  onRestore: () => void;
  onDelete: () => void;
}

/**
 * Overflow "More actions" menu next to the inline "View activity"
 * button. Groups actions into three semantic blocks separated by
 * dividers:
 *
 *   1. Profile editing       — Edit name, Edit roles
 *   2. Credentials           — Reset password OR Resend invitation
 *                              (mutually exclusive based on whether
 *                              the user has set their first password)
 *   3. Lifecycle             — Disable / Re-enable
 *
 * Each item gets an icon + a full label so users never have to guess
 * what an icon-only button does.
 */
function UserActionsMenu({
  user,
  isSelf,
  onEditProfile,
  onEditRoles,
  onApprove,
  onReject,
  onResetPassword,
  onResendInvitation,
  onDisable,
  onEnable,
  onRestore,
  onDelete,
}: UserActionsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);
  const wrap = (cb: () => void) => () => {
    handleClose();
    cb();
  };

  // Resend Invitation is offered for any account that hasn't been
  // claimed yet — both `pending_setup` (link still valid, but admin
  // wants to nudge) and `link_expired` (the rep cannot proceed
  // without a new link).  Falls back to must_change_password if the
  // backend hasn't populated setup_status for some reason.
  const showResend = user.setup_status
    ? user.setup_status !== 'completed'
    : user.must_change_password;
  const showReset = user.status === 'active' && user.setup_status === 'completed';
  const showApprove = user.status === 'pending';
  const showReject = user.status === 'pending';
  const showDisable = user.status === 'active';
  const showEnable = user.status === 'disabled';
  const showRestore = user.status === 'rejected';
  // Delete is destructive and offered for every account *except* the
  // signed-in admin themselves — preventing self-lockout via the UI.
  // The backend enforces the same rule plus the last-admin guard.
  const showDelete = !isSelf;

  return (
    <>
      <Tooltip title="More actions" arrow>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="More actions"
          aria-haspopup="true"
          aria-expanded={open}
          sx={{
            color: 'rgba(15, 10, 7, 0.55)',
            bgcolor: 'transparent',
            border: '1px solid rgba(15, 10, 7, 0.12)',
            borderRadius: 1,
            width: 30,
            height: 30,
            '&:hover': {
              color: GOLD,
              borderColor: 'rgba(187, 140, 63, 0.5)',
              bgcolor: 'rgba(187, 140, 63, 0.06)',
            },
          }}
        >
          <MoreHorizIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              position: 'relative',
              mt: 0.75,
              width: 'auto',
              minWidth: 240,
              maxWidth: 300,
              borderRadius: 1.5,
              bgcolor: IVORY_LIGHT,
              backgroundImage: 'none',
              border: '1px solid rgba(187, 140, 63, 0.25)',
              boxShadow: '0 14px 32px rgba(142, 106, 42, 0.12)',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: `linear-gradient(to right, ${GOLD} 0%, ${GOLD_LIGHT} 50%, transparent 100%)`,
                zIndex: 1,
              },
              '& .MuiList-root': { py: 0.75 },
              '& .MuiMenuItem-root': {
                fontFamily: FONT_INTER,
                fontSize: 13,
                fontWeight: 500,
                mx: 0.75,
                px: 1.25,
                py: 0.85,
                gap: 1.25,
                borderRadius: 1,
                transition: 'background-color 140ms ease, color 140ms ease',
              },
              '& .MuiListItemIcon-root': {
                minWidth: 0,
                color: GOLD,
              },
            },
          },
        }}
      >
        {showApprove && <ActionGroupLabel>Sign-up Review</ActionGroupLabel>}
        {showApprove && (
          <MenuItem
            onClick={wrap(onApprove)}
            sx={{
              color: '#22865a',
              '& .MuiListItemIcon-root': { color: '#22865a' },
              '&:hover': {
                bgcolor: 'rgba(34, 134, 90, 0.10)',
              },
            }}
          >
            <ListItemIcon>
              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary="Approve Sign-up"
              secondary="Activate the account so the user can sign in"
              secondaryTypographyProps={{
                sx: {
                  fontFamily: FONT_INTER,
                  fontSize: 11,
                  color: 'rgba(15, 10, 7, 0.55)',
                  whiteSpace: 'normal',
                  mt: 0.15,
                },
              }}
            />
          </MenuItem>
        )}
        {showReject && (
          <MenuItem
            onClick={wrap(onReject)}
            sx={{
              color: '#b22f2f',
              '& .MuiListItemIcon-root': { color: '#b22f2f' },
              '&:hover': {
                bgcolor: 'rgba(178, 47, 47, 0.08)',
              },
            }}
          >
            <ListItemIcon>
              <DoDisturbAltIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary="Reject Sign-up"
              secondary="Decline this request (reversible via Restore)"
              secondaryTypographyProps={{
                sx: {
                  fontFamily: FONT_INTER,
                  fontSize: 11,
                  color: 'rgba(15, 10, 7, 0.55)',
                  whiteSpace: 'normal',
                  mt: 0.15,
                },
              }}
            />
          </MenuItem>
        )}
        {(showApprove || showReject) && <GoldDivider />}

        <ActionGroupLabel>Profile</ActionGroupLabel>
        <MenuItem
          onClick={wrap(onEditProfile)}
          sx={{
            color: INK,
            '&:hover': {
              bgcolor: 'rgba(187, 140, 63, 0.10)',
              '& .MuiListItemIcon-root': { color: '#8e6a2a' },
            },
          }}
        >
          <ListItemIcon>
            <DriveFileRenameOutlineIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText primary="Edit Name" />
        </MenuItem>
        <MenuItem
          onClick={wrap(onEditRoles)}
          sx={{
            color: INK,
            '&:hover': {
              bgcolor: 'rgba(187, 140, 63, 0.10)',
              '& .MuiListItemIcon-root': { color: '#8e6a2a' },
            },
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText primary="Edit Roles" />
        </MenuItem>

        {(showReset || showResend) && [
          <GoldDivider key="div-creds" />,
          <ActionGroupLabel key="lbl-creds">Credentials</ActionGroupLabel>,
        ]}
        {showResend && (
          <MenuItem
            onClick={wrap(onResendInvitation)}
            sx={{
              color: INK,
              '&:hover': {
                bgcolor: 'rgba(187, 140, 63, 0.10)',
                '& .MuiListItemIcon-root': { color: '#8e6a2a' },
              },
            }}
          >
            <ListItemIcon>
              <MailOutlineIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary="Resend Invitation"
              secondary="They never finished setting their password"
              secondaryTypographyProps={{
                sx: {
                  fontFamily: FONT_INTER,
                  fontSize: 11,
                  color: 'rgba(15, 10, 7, 0.55)',
                  whiteSpace: 'normal',
                  mt: 0.15,
                },
              }}
            />
          </MenuItem>
        )}
        {showReset && (
          <MenuItem
            onClick={wrap(onResetPassword)}
            sx={{
              color: INK,
              '&:hover': {
                bgcolor: 'rgba(187, 140, 63, 0.10)',
                '& .MuiListItemIcon-root': { color: '#8e6a2a' },
              },
            }}
          >
            <ListItemIcon>
              <LockResetIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary="Send Password Reset"
              secondary="Forces a new password on next sign-in"
              secondaryTypographyProps={{
                sx: {
                  fontFamily: FONT_INTER,
                  fontSize: 11,
                  color: 'rgba(15, 10, 7, 0.55)',
                  whiteSpace: 'normal',
                  mt: 0.15,
                },
              }}
            />
          </MenuItem>
        )}

        {(showDisable || showEnable || showRestore) && [
          <GoldDivider key="div-lifecycle" />,
          <ActionGroupLabel key="lbl-lifecycle">Account</ActionGroupLabel>,
        ]}
        {showRestore && (
          <MenuItem
            onClick={wrap(onRestore)}
            sx={{
              color: '#326eb8',
              '& .MuiListItemIcon-root': { color: '#326eb8' },
              '&:hover': {
                bgcolor: 'rgba(50, 110, 184, 0.10)',
              },
            }}
          >
            <ListItemIcon>
              <RestoreIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary="Restore Account"
              secondary="Reverses the earlier rejection"
              secondaryTypographyProps={{
                sx: {
                  fontFamily: FONT_INTER,
                  fontSize: 11,
                  color: 'rgba(15, 10, 7, 0.55)',
                  whiteSpace: 'normal',
                  mt: 0.15,
                },
              }}
            />
          </MenuItem>
        )}
        {showDisable && (
          <MenuItem
            onClick={wrap(onDisable)}
            sx={{
              color: '#a07530',
              '& .MuiListItemIcon-root': { color: '#a07530' },
              '&:hover': {
                bgcolor: 'rgba(160, 117, 48, 0.10)',
              },
            }}
          >
            <ListItemIcon>
              <BlockIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText primary="Disable Account" />
          </MenuItem>
        )}
        {showEnable && (
          <MenuItem
            onClick={wrap(onEnable)}
            sx={{
              color: '#22865a',
              '& .MuiListItemIcon-root': { color: '#22865a' },
              '&:hover': {
                bgcolor: 'rgba(34, 134, 90, 0.10)',
              },
            }}
          >
            <ListItemIcon>
              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText primary="Re-enable Account" />
          </MenuItem>
        )}

        {showDelete && [
          <GoldDivider key="div-danger" />,
          <ActionGroupLabel key="lbl-danger">Danger Zone</ActionGroupLabel>,
          <MenuItem
            key="item-delete"
            onClick={wrap(onDelete)}
            sx={{
              color: '#b22f2f',
              '& .MuiListItemIcon-root': { color: '#b22f2f' },
              '&:hover': {
                bgcolor: 'rgba(178, 47, 47, 0.08)',
              },
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
            <ListItemText
              primary="Delete User"
              secondary="Frees email for reuse · preserves audit history"
              secondaryTypographyProps={{
                sx: {
                  fontFamily: FONT_INTER,
                  fontSize: 11,
                  color: 'rgba(15, 10, 7, 0.55)',
                  whiteSpace: 'normal',
                  mt: 0.15,
                },
              }}
            />
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
}

/** Tiny brand-gold uppercase section heading inside the actions menu. */
function ActionGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        px: 2,
        pt: 0.85,
        pb: 0.4,
        fontFamily: FONT_INTER,
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color: GOLD,
      }}
    >
      {children}
    </Box>
  );
}

/** Gold-tinted divider between menu sections, matching the rest of
 *  the admin section's hairline accents. */
function GoldDivider() {
  return (
    <Divider
      component="li"
      sx={{
        mx: 1.5,
        my: 0.5,
        borderColor: 'rgba(187, 140, 63, 0.22)',
      }}
    />
  );
}

type BadgeTone = { label: string; bg: string; fg: string; dot: string };

const STATUS_TONE: Record<UserListItem['status'], BadgeTone> = {
  active: { label: 'Active', bg: 'rgba(34, 134, 90, 0.12)', fg: '#22865a', dot: '#22865a' },
  // "Awaiting Approval" rather than the raw "Pending" so it's
  // unambiguously distinct from the "Pending Setup" setup-status chip,
  // and lines up with the Awaiting-approval KPI chip above.
  pending: {
    label: 'Awaiting Approval',
    bg: 'rgba(91, 107, 199, 0.12)',
    fg: '#3f4d99',
    dot: '#5b6bc7',
  },
  disabled: { label: 'Disabled', bg: 'rgba(168, 107, 30, 0.12)', fg: '#a86b1e', dot: '#a86b1e' },
  rejected: { label: 'Rejected', bg: 'rgba(178, 47, 47, 0.12)', fg: '#b22f2f', dot: '#b22f2f' },
};

// Setup-status overrides — applied when the user is otherwise
// "active" but hasn't actually claimed their account yet.  Keeps
// the chip honest: the user can't sign in (link still valid or
// expired) so "Active" would be misleading.
const SETUP_TONE: Record<'pending_setup' | 'link_expired', BadgeTone> = {
  pending_setup: {
    label: 'Pending Setup',
    bg: 'rgba(187, 140, 63, 0.14)',
    fg: '#a07530',
    dot: '#bb8c3f',
  },
  link_expired: {
    label: 'Link Expired',
    bg: 'rgba(178, 47, 47, 0.12)',
    fg: '#b22f2f',
    dot: '#b22f2f',
  },
};

function StatusBadge({
  status,
  setupStatus,
}: {
  status: UserListItem['status'];
  setupStatus?: UserListItem['setup_status'];
}) {
  // Setup override beats the raw status — for an "active" account
  // that hasn't been claimed, "Pending Setup" / "Link Expired" is
  // the actionable truth.  We still defer to the raw status for
  // disabled / rejected / pending — those are admin-driven flows
  // that override setup completion.
  const override =
    status === 'active' && setupStatus && setupStatus !== 'completed'
      ? SETUP_TONE[setupStatus]
      : null;
  const t = override ?? STATUS_TONE[status];
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        px: 0.95,
        py: 0.4,
        borderRadius: 999,
        bgcolor: t.bg,
        color: t.fg,
      }}
    >
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: t.dot }} />
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
        }}
      >
        {t.label}
      </Typography>
    </Box>
  );
}

function RoleTag({ label }: { label: string }) {
  return (
    <Typography
      sx={{
        fontFamily: FONT_INTER,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.04em',
        color: GOLD,
        bgcolor: 'rgba(187, 140, 63, 0.10)',
        border: '1px solid rgba(187, 140, 63, 0.22)',
        px: 0.85,
        py: 0.3,
        borderRadius: 0.75,
      }}
    >
      {label}
    </Typography>
  );
}

/**
 * Render the user's role assignments for the table cell.
 *
 * Honors the admin-wildcard rule: if the user has the ``admin`` role,
 * the redundant section roles are suppressed and a single
 * Administrator + "All-access" affordance is shown — followed by any
 * role admin doesn't imply (Data Upload, Regional Sales Representative;
 * see auth/roles.ts). Matches the Add user / Edit roles dialogs.
 */
function RolesCell({ roles }: { roles: UserListItem['roles'] }) {
  const isAdmin = roles.some((r) => r.name === 'admin');
  const alongside = roles.filter((r) => ROLES_ALONGSIDE_ADMIN.has(r.name));

  if (roles.length === 0) {
    return (
      <Typography sx={{ fontFamily: FONT_INTER, fontSize: 11.5, color: 'rgba(15, 10, 7, 0.4)' }}>
        none
      </Typography>
    );
  }

  if (isAdmin) {
    return (
      <Tooltip
        title="Administrator implicitly includes access to every section."
        arrow
        placement="top-start"
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <RoleTag label="Administrator" />
          <Box
            sx={{
              px: 0.7,
              py: 0.2,
              borderRadius: 0.6,
              bgcolor: 'rgba(187, 140, 63, 0.16)',
              color: '#8e6a2a',
              fontFamily: FONT_INTER,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}
          >
            All-access
          </Box>
          {alongside.map((r) => (
            <RoleTag key={r.id} label={r.display_name} />
          ))}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {roles.map((r) => (
        <RoleTag key={r.id} label={r.display_name} />
      ))}
    </Box>
  );
}

function EmptyState({ onNewUser, hasFilters }: { onNewUser: () => void; hasFilters: boolean }) {
  return (
    <Box sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          bgcolor: 'rgba(187, 140, 63, 0.10)',
          color: GOLD,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PersonOffIcon sx={{ fontSize: 28 }} />
      </Box>
      <Typography
        sx={{
          fontFamily: FONT_INTER,
          fontSize: 16,
          fontWeight: 600,
          color: INK,
        }}
      >
        {hasFilters ? 'No users match those filters' : 'No users yet'}
      </Typography>
      <Typography
        sx={{
          mt: 0.5,
          fontFamily: FONT_INTER,
          fontSize: 13.5,
          color: 'rgba(15, 10, 7, 0.6)',
          maxWidth: 440,
          mx: 'auto',
        }}
      >
        {hasFilters
          ? 'Try clearing or adjusting the search and filter fields.'
          : 'Create the first account to start onboarding your team.'}
      </Typography>
      {!hasFilters && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNewUser}
          sx={{
            mt: 2.5,
            background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, #8e6a2a 100%)`,
            color: INK,
            fontFamily: FONT_INTER,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: 12,
            px: 2.25,
            py: 0.85,
            boxShadow: 'none',
            '&:hover': {
              background: `linear-gradient(135deg, #efc960 0%, #c79747 55%, #936e2b 100%)`,
              boxShadow: '0 4px 14px rgba(187, 140, 63, 0.22)',
            },
          }}
        >
          Create user
        </Button>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------
// Sortable column header
// ---------------------------------------------------------------------

interface SortableHeaderProps {
  label: string;
  sortKey: UserSortKey;
  currentSort: UserSortKey;
  currentDir: SortDir;
  onSort: (key: UserSortKey) => void;
}

function SortableHeader({ label, sortKey, currentSort, currentDir, onSort }: SortableHeaderProps) {
  const active = currentSort === sortKey;
  return (
    <TableCell
      onClick={() => onSort(sortKey)}
      sx={{
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'color 160ms ease',
        '&:hover': { color: 'rgba(15, 10, 7, 0.78) !important' },
      }}
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        {label}
        {active &&
          (currentDir === 'asc' ? (
            <ArrowDropUpIcon sx={{ fontSize: 18, color: GOLD, ml: -0.25 }} />
          ) : (
            <ArrowDropDownIcon sx={{ fontSize: 18, color: GOLD, ml: -0.25 }} />
          ))}
      </Box>
    </TableCell>
  );
}
